import * as ToastPrimitive from 'radix-ui/toast';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '../Button/Button';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { OpenWord } from '../OpenWord/OpenWord';

/**
 * Toasts. Bottom of the screen on a phone (above the nav bar, in thumb reach),
 * top-right on a desktop. Errors do not auto-dismiss: the one message a user
 * must be able to re-read is the one that says the write failed.
 */
export type ToastTone = 'neutral' | 'success' | 'danger';

/**
 * ONE ACTION BESIDE THE MESSAGE, AND IT IS A REVERSAL (wave 5 round 3, owner finding C4-1).
 *
 * The HIG's undo page asks a screen to "Show the results of an undo or redo ... it's crucial
 * to highlight the result of each undo and redo to keep people from thinking that the action
 * had no effect" (hig:
 * https://developer.apple.com/design/human-interface-guidelines/undo-and-redo), which a
 * message can only do if the way back is ON it. `altText` is the sentence a screen reader is
 * given when the message cannot be shown as a control - Radix requires it, and it is the one
 * place this primitive asks the caller for words it cannot derive.
 */
export interface ToastAction {
  readonly label: string;
  readonly altText: string;
  readonly onAction: () => void;
}

export interface ToastInput {
  readonly title: string;
  readonly description?: string | undefined;
  readonly tone?: ToastTone | undefined;
  readonly action?: ToastAction | undefined;
  /**
   * The OPEN id of a title nobody has decided yet (DESIGN.md D4, Part 3 item 6): the title is
   * drawn dotted under it (`OpenWord`).
   */
  readonly openWord?: string | undefined;
}

interface ToastRecord extends ToastInput {
  readonly id: number;
}

/**
 * PUSHING A TOAST HANDS BACK THE WAY TO TAKE IT AWAY AGAIN.
 *
 * A caller that offers a reversal owns the moment it stops being true - an undo performed by
 * some other path, a second completion that replaces the first - and until now it had no way
 * to say so, so a message could stand on the screen offering to undo something already
 * undone. The handle is idempotent and safe after the toast has closed on its own.
 */
export type ToastHandle = () => void;

const ToastContext = createContext<((input: ToastInput) => ToastHandle) | null>(null);

const TONE_ICON: Record<ToastTone, IconName> = {
  neutral: 'info',
  success: 'success',
  danger: 'warning',
};

const TONE_INK: Record<ToastTone, string> = {
  neutral: 'text-ink-muted',
  success: 'text-success',
  danger: 'text-danger',
};

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  /**
   * THE ID IS A COUNTER RATHER THAN A CLOCK, because the handle has to name the toast it
   * belongs to from OUTSIDE the state updater: `Date.now() + current.length` was computed
   * inside one, where the caller could not see it, and two toasts pushed in the same
   * millisecond from different call sites would have shared a number.
   */
  const nextId = useRef(0);

  const push = useCallback(
    (input: ToastInput): ToastHandle => {
      nextId.current += 1;
      const id = nextId.current;
      setToasts((current) => [...current, { ...input, id }]);
      return () => dismiss(id);
    },
    [dismiss],
  );

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => {
          const tone = toast.tone ?? 'neutral';
          return (
            <ToastPrimitive.Root
              key={toast.id}
              duration={tone === 'danger' ? Number.POSITIVE_INFINITY : 5000}
              onOpenChange={(open) => {
                if (!open) {
                  dismiss(toast.id);
                }
              }}
              className="chrome-raised chrome-toast flex items-start gap-3 rounded-surface border border-line p-3 shadow-popover"
            >
              {/* The tone icon sits in a box the height of the title's line, so
                  it centres on the first line without a hand-tuned offset. */}
              <span className={cx('flex h-5 shrink-0 items-center', TONE_INK[tone])}>
                <Icon name={TONE_ICON[tone]} size="1.15rem" />
              </span>
              <div className="min-w-0 flex-1">
                <ToastPrimitive.Title className="text-sm font-medium text-ink">
                  {toast.openWord === undefined ? (
                    toast.title
                  ) : (
                    <OpenWord id={toast.openWord}>{toast.title}</OpenWord>
                  )}
                </ToastPrimitive.Title>
                {toast.description !== undefined && (
                  <ToastPrimitive.Description className="mt-0.5 text-xs text-ink-muted">
                    {toast.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              {toast.action !== undefined && (
                /*
                  THE ACTION SITS BEFORE THE CLOSE, which is the order every macOS control
                  row is read in: what the message offers, then the way to make it go away.
                  `secondary` is the neutral glass capsule this product gives a control that
                  is not the page's main action, and `sm` is its desktop chrome size with the
                  40px press region the token floor keeps under it.
                */
                <ToastPrimitive.Action altText={toast.action.altText} asChild>
                  <Button variant="secondary" size="sm" onClick={toast.action.onAction}>
                    {toast.action.label}
                  </Button>
                </ToastPrimitive.Action>
              )}
              <ToastPrimitive.Close asChild>
                <IconButton icon="close" label="Dismiss" size="sm" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        {/*
          THE PHONE'S TOAST RESTS ON THE TAB BAR'S TOP EDGE, and the number it is held off
          by is the bar's own (wave 5 round 3, found by review check "E4 completing a task
          offers Undo at 375": the message ended at y 736 and the bar began at y 729, so it
          stood 7px under a floating bar).

          THE CAUSE was a legacy token. `--bottom-nav-height` is 60px and predates the
          bar's kit geometry; the bar floats at `--shell-tab-height` 62 plus
          `--shell-tab-inset-bottom` 21 plus the safe area, which is exactly what
          `--shell-phone-content-inset` states and what every other scrolling surface on the
          phone already uses (index.css). This viewport was its last reader, so the toast
          was measured against a bar that has not been that size since wave 4.
        */}
        <ToastPrimitive.Viewport
          className={cx(
            'layer-toast fixed right-0 bottom-0 left-0 flex w-full flex-col gap-2 p-4',
            'pb-[calc(var(--shell-phone-content-inset)+var(--space-4))]',
            'md:top-0 md:bottom-auto md:left-auto md:w-96 md:pb-4',
          )}
        />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

/** Throws when used outside the provider: a silently dropped toast hides errors. */
export function useToast(): (input: ToastInput) => ToastHandle {
  const push = useContext(ToastContext);
  if (push === null) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return push;
}
